"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { animate, useInView } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";

import type { PagePatrickAffiliateSchema } from "@/sanity/pages/PagePatrickAffiliateTemplate";
import { measuredRichTextDark } from "./richtext";
import ChevronCta from "./ChevronCta";

import waveDots from "@/public/fabio-affiliate/wave-dots.png";

/**
 * Card geometry, straight off the Figma frame (8841:1778 desktop, 8811:4451
 * mobile). The design is drawn at 1920, where 1rem === 8px, so every Figma px
 * converts as px / 8.
 */
const CARD_HEIGHT_PX = 316;
/** Perforated left edge: 9 holes, first centre 20.652px, 25.746px apart. */
const HOLE_COUNT = 9;
const HOLE_FIRST_PX = 20.652;
const HOLE_PITCH_PX = 25.746;
/** Circle radius 11.4426px, centred 2.8607px *outside* the card's left edge. */
const HOLE_RADIUS_REM = 1.43;
const HOLE_CENTER_X_REM = -0.3575;
/** Length of the teal hairline on the top edge — full-ish on the first card, half on the rest. */
const ACCENT_WIDTH_FIRST = "87.15%";
const ACCENT_WIDTH_REST = "48.39%";

const HOLE_PERCENTS = Array.from(
  { length: HOLE_COUNT },
  (_, n) => ((HOLE_FIRST_PX + n * HOLE_PITCH_PX) / CARD_HEIGHT_PX) * 100,
);

const MASK_GRADIENTS = HOLE_PERCENTS.map(
  (p) => `radial-gradient(circle ${HOLE_RADIUS_REM}rem at ${HOLE_CENTER_X_REM}rem ${p}%, transparent 99%, #000 100%)`,
).join(",");

function CountUpValue({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  const parsed = useMemo(() => {
    // Draft mode interleaves stega characters into every string, which would
    // make the numeric match below fail and freeze the counter at its label.
    const clean = stegaClean(value) ?? "";
    const match = clean.match(/^(\D*)([\d.,]+)(\D*)$/);
    if (!match) return null;
    const [, prefix, numStr, suffix] = match;
    const hasComma = numStr.includes(",");
    const cleanNum = numStr.replace(/,/g, "");
    const decimalIdx = cleanNum.indexOf(".");
    const decimals = decimalIdx >= 0 ? cleanNum.length - decimalIdx - 1 : 0;
    const target = parseFloat(cleanNum);
    if (isNaN(target)) return null;
    return { prefix, suffix, hasComma, decimals, target };
  }, [value]);

  const format = (n: number) => {
    if (!parsed) return value;
    const { prefix, suffix, hasComma, decimals } = parsed;
    const num = hasComma
      ? n.toLocaleString("en-US", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      : n.toFixed(decimals);
    return `${prefix}${num}${suffix}`;
  };

  const [display, setDisplay] = useState(() => (parsed ? format(0) : value));

  useEffect(() => {
    if (!inView || !parsed) return;
    const controls = animate(0, parsed.target, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(format(latest)),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, parsed]);

  return (
    <span
      ref={ref}
      className={className}>
      {display}
    </span>
  );
}

export default function VipDealSection({ data }: { data: PagePatrickAffiliateSchema }) {
  if (!data?.vipDealTitle?.length && !data?.vipDealItems?.length && !data?.vipDealCTA) return null;

  return (
    <section className="">
      <div className="relative mx-auto flex flex-col items-center gap-[4rem] overflow-clip bg-[#001B14] px-[2rem] py-[8rem] text-center lg:px-0 lg:py-[17.5rem]">
        <Image
          src={waveDots}
          alt=""
          className="pointer-events-none absolute inset-0 !h-full !w-full object-cover"
        />

        <div className="relative flex w-full flex-col items-center gap-[4rem] lg:w-[152.3rem]">
          <div className="flex flex-col items-center justify-center gap-[1rem]">
            {data?.vipDealCalloutText && (
              <p className="m-0 font-blacker text-[2rem] leading-[1.5] text-white italic">{data.vipDealCalloutText}</p>
            )}
            {/* Highlight: 42px wash on mobile (47 elsewhere), no left-rule
                overshoot, and the design's double spaces — both sides on desktop,
                after the word on mobile. */}
            {data?.vipDealTitle?.length > 0 && (
              <div className="richtext richtext-center [--hl-ext:0rem] [--hl-ml:0.05em] [--hl-mr:0.22em] [--hl-wash:5.25rem] lg:[--hl-ml:0.2em] lg:[--hl-mr:0.23em] [--hl-tracking:0em] [--hl-dy:0.3125rem] lg:[--hl-dy:0.125rem] lg:[--hl-wash:9.25rem] prose-h2:font-geist prose-h2:font-[500] prose-h2:text-[4.75rem] prose-h2:leading-[1.15] prose-h2:tracking-[-0.2375rem] prose-h2:text-white lg:prose-h2:text-[8rem] lg:prose-h2:tracking-[-0.4rem]">
                <PortableText
                  value={data.vipDealTitle}
                  components={measuredRichTextDark}
                />
              </div>
            )}
          </div>

          {data?.vipDealItems?.length > 0 && (
            <div className="flex w-full flex-col items-center justify-center gap-[5rem] lg:flex-row">
              {data.vipDealItems.map((item, i) => {
                // The first card centres its figure; the others hang it off a
                // fixed left inset, exactly as the frame is built.
                const centred = i === 0;
                return (
                  <div
                    key={`${item?.label ?? "item"}-${i}`}
                    className="relative h-[30.25rem] w-[min(47.375rem,100%)] lg:h-[39.5rem] lg:w-[47.29rem]">
                    <div
                      className={`flex relative flex-col justify-center w-full h-full bg-white/[3%] backdrop-blur-[0.179rem] border-white/[4%] border pt-[5.72rem] pb-[3.04rem] ${
                        centred ? "items-center px-[3.75rem]" : "items-start pl-[8.05rem] pr-[3.75rem]"
                      }`}
                      style={{
                        WebkitMaskImage: MASK_GRADIENTS,
                        maskImage: MASK_GRADIENTS,
                        WebkitMaskComposite: "source-in",
                        maskComposite: "intersect",
                      }}>
                      {/* Top edge: a faint full-width rule with a teal accent over it. */}
                      <div className="pointer-events-none absolute top-0 left-0 h-[0.134rem] w-full bg-white/[12%]" />
                      <div
                        className="pointer-events-none absolute top-0 left-0 h-[0.134rem] bg-[#75D4B9]"
                        style={{
                          width: centred ? ACCENT_WIDTH_FIRST : ACCENT_WIDTH_REST,
                        }}
                      />

                      {/* Figma puts the centred card's pair at x=88.5 in a 378.3
                          card — dead centre. The other two hang off the left inset,
                          which is where their 17.16px label indent comes from. */}
                      <div className={`flex flex-col ${centred ? "items-center" : "items-start"}`}>
                        <div className="flex items-baseline">
                          {item?.value && (
                            <CountUpValue
                              value={item.value}
                              className="bg-[linear-gradient(180deg,#D6EEE7_35.39%,rgba(255,255,255,0.00)_166.01%)] bg-clip-text pr-[2rem] font-geist font-[500] text-[15rem] leading-none tracking-[-0.9rem] text-transparent tabular-nums lg:text-[22.35rem] lg:tracking-[-1.34rem]"
                            />
                          )}
                          {item?.unit && (
                            <span className="font-geist font-[500] text-[9.67rem] tracking-[-0.483rem] text-white/[36%]">
                              {item.unit}
                            </span>
                          )}
                        </div>
                        {item?.label && (
                          // "for the first 50k account" fills 201 of its 202px, so
                          // the other cards' indent was what broke it onto two lines.
                          <span
                            className={`text-[#D6EEE7]/90 font-[400] font-geist leading-[1.33] text-[2.25rem] ${
                              centred ? "w-[25.25rem] text-center" : "pl-[2.15rem]"
                            }`}>
                            {item.label}
                          </span>
                        )}
                      </div>
                    </div>

                    {HOLE_PERCENTS.map((p) => (
                      <div
                        key={p}
                        className="pointer-events-none absolute h-[2.86rem] w-[2.86rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5"
                        style={{
                          left: `${HOLE_CENTER_X_REM}rem`,
                          top: `${p}%`,
                          clipPath: "inset(0 0 0 50%)",
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {data?.vipDealCTA && (
            <ChevronCta
              data={data.vipDealCTA}
              variant="white"
              language={data?.settings?.language}
            />
          )}
        </div>
      </div>
    </section>
  );
}
